import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5004/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear auth data on unauthorized
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // Redirect to login if not already there
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email: string, password: string) => api.post('/auth/login', { email, password }),
  register: (data: { username: string; email: string; password: string; role?: string; permissions?: any }) =>
    api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
  verify: () => api.get('/auth/verify'),
};

// Accounts API
export const accountsAPI = {
  getAll: () => api.get('/accounts'),
  create: (data: { phoneNumber: string; accountName: string }) =>
    api.post('/accounts', data),
  delete: (id: string) => api.delete(`/accounts/${id}`),
  getStatus: (id: string) => api.get(`/accounts/${id}/status`),
};

// Groups API
export const groupsAPI = {
  getAll: (params?: { search?: string; limit?: number; fields?: 'minimal' | string }) => 
    api.get('/groups', { params }),
  getByAccount: (accountId: string) => api.get(`/groups/account/${accountId}`),
  getById: (id: string) => api.get(`/groups/${id}`),
  getStats: () => api.get('/groups/stats/overview'),
  getArchived: () => api.get('/groups/archived'),
  exportGroupCSV: (groupId: string, params?: { search?: string }) =>
    api.get(`/groups/${groupId}/export/csv`, { 
      params,
      responseType: 'blob' // Important for file download
    }),
  exportAllCSV: (params?: { search?: string; accountId?: string; type?: 'active' | 'archived' }) =>
    api.get('/groups/export/csv', { 
      params,
      responseType: 'blob' // Important for file download
    }),
};

// Analytics API
export const analyticsAPI = {
  getDashboard: () => api.get('/analytics/dashboard'),
  getMemberOverlap: () => api.get('/analytics/member-overlap'),
  getAccountComparison: () => api.get('/analytics/account-comparison'),
};

// Messages API
export const messagesAPI = {
  getByGroup: (groupId: string, params?: { limit?: number; page?: number; startDate?: string; endDate?: string }) =>
    api.get(`/messages/group/${groupId}`, { params }),
  getByGroupId: (id: string, params?: { limit?: number; page?: number; startDate?: string; endDate?: string }) =>
    api.get(`/messages/group-by-id/${id}`, { params }),
  getByAccount: (accountId: string, params?: { limit?: number; page?: number; startDate?: string; endDate?: string }) =>
    api.get(`/messages/account/${accountId}`, { params }),
  getGroupStats: (groupId: string, params?: { startDate?: string; endDate?: string }) =>
    api.get(`/messages/stats/group/${groupId}`, { params }),
};

// Members API
export const membersAPI = {
  getAll: (params?: { search?: string; accountId?: string; page?: number; limit?: number; minGroups?: number }) =>
    api.get('/members', { params }),
  getByPhoneNumber: (phoneNumber: string) =>
    api.get(`/members/${phoneNumber}`),
  update: (phoneNumber: string, data: { name: string; isAdmin?: boolean; isSuperAdmin?: boolean }) =>
    api.put(`/members/${phoneNumber}`, data),
  getArchived: (params?: { search?: string; accountId?: string; page?: number; limit?: number; minGroups?: number }) =>
    api.get('/members/archived', { params }),
  exportCSV: (
    params?: { search?: string; accountId?: string; minGroups?: number; type?: 'active' | 'archived'; includeGroups?: boolean },
    config?: { onDownloadProgress?: (progressEvent: { loaded: number; total?: number }) => void }
  ) =>
    api.get('/members/export/csv', {
      params,
      responseType: 'blob',
      timeout: 600000, // 10 minutes for large exports
      ...(config?.onDownloadProgress && { onDownloadProgress: config.onDownloadProgress }),
    }),
};

// Comparison API
export const comparisonAPI = {
  uploadCSV: (groupId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/comparison/group/${groupId}/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  getComparison: (groupId: string, params?: { page?: number; limit?: number; search?: string; status?: 'present' | 'not_present'; summaryOnly?: boolean }) => 
    api.get(`/comparison/group/${groupId}`, { params }),
  reanalyze: (groupId: string) => api.post(`/comparison/group/${groupId}/reanalyze`),
  deleteComparison: (groupId: string) => api.delete(`/comparison/group/${groupId}`),
};

// Admin API
export const adminAPI = {
  getUsers: () => api.get('/admin/users'),
  getUser: (id: string) => api.get(`/admin/users/${id}`),
  createUser: (data: { username: string; email: string; password: string; role?: string; permissions?: any; isActive?: boolean }) =>
    api.post('/admin/users', data),
  updateUser: (id: string, data: any) => api.put(`/admin/users/${id}`, data),
  deleteUser: (id: string) => api.delete(`/admin/users/${id}`),
  getRoles: () => api.get('/admin/roles'),
  getPermissions: () => api.get('/admin/permissions'),
};

// Custom Groups API
export const customGroupsAPI = {
  getAll: () => api.get('/custom-groups'),
  getById: (id: string) => api.get(`/custom-groups/${id}`),
  create: (data: { name: string; description?: string; type?: 'analysis' | 'report'; attachedGroups?: string[] }) =>
    api.post('/custom-groups', data),
  update: (id: string, data: { name?: string; description?: string; type?: 'analysis' | 'report'; attachedGroups?: string[] }) =>
    api.put(`/custom-groups/${id}`, data),
  delete: (id: string) => api.delete(`/custom-groups/${id}`),
  getAnalytics: (id: string) => api.get(`/custom-groups/${id}/analytics`),
};

export default api;

